import type { RouteResource } from '@townsquare/router/routing-types';
import { notFoundRouterRoute } from '@townsquare/404-page-view/__router-route';
import { appsResource } from '@townsquare/apps-store/resource';
import { appsRoute } from '@townsquare/apps-view/__router-route';
import { popupAuthRoute } from '@townsquare/auth/popup-login';
import { capabilityProviderResource } from '@townsquare/capability-provider/resource';
import { emptyOrNonProvisionedStatusUpdateStateRouterRoute } from '@townsquare/empty-or-not-provisioned-state/__router-route';
import {
	projectsAppUpdateViewRouterRoute,
	projectsAppSavedViewsUpdatesRouterRoute,
} from '@townsquare/home-view/routes';
import { notificationsRouterRoute } from '@townsquare/notifications-view/__router-route';
import projectViewRouteDefinition from '@townsquare/project-view/route-definition';
import {
	projectDirectoryFollowingRouterRoute,
	projectDirectoryRouterRoute,
	projectNoWorkspaceDirectoryRouterRoute,
	projectDirectoryArchivedRouterRoute,
} from '@townsquare/projects-directory-view/__router-route';
import { applyResourcesToRoute, expandGlobalRoute } from '@townsquare/router/create-route';
import type { HomepageRouteType, HomepageRoutes } from '@townsquare/router/route-types';
import { toRRRRoute } from '@townsquare/router/volt/adapter';
import { projectsAppReadingModeRoute } from '@townsquare/routes/reading-mode-view';
import { projectsAppGlobalSearchRoute } from '@townsquare/routes/search-projects-app';
import { rovoEntitlementResource } from '@townsquare/rovo/entitlement';
import { searchConfigurationResource } from '@townsquare/search-config-resource';
import {
	projectSettingsRouterRoute,
	noWorkspacePersonalSettingsRouterRoute,
} from '@townsquare/settings-view/__router-route';
import { followProjectActionRouterRoute } from '@townsquare/url-actions/follow-project/__router-route';
import { lookupRedirectActionRouterRoute } from '@townsquare/url-actions/lookup-redirect/__router-route';
import { reactionUpdateActionRouterRoute } from '@townsquare/url-actions/reaction-update/__router-route';
import { searchRedirectActionRouterRoute } from '@townsquare/url-actions/search-redirect/__router-route';
import { projectCapabilitiesResource } from '@townsquare/project-capabilities/resource';

import entrypointRegistry from './entrypoint-registry.generated';

const projectRoute = toRRRRoute(projectViewRouteDefinition, entrypointRegistry[projectViewRouteDefinition.entrypoint]);

// When adding a new route, please also add it to the route-sanity-check E2E test
export const routeDefinitions: HomepageRoutes = [
	// Actions
	followProjectActionRouterRoute,
	lookupRedirectActionRouterRoute,
	reactionUpdateActionRouterRoute,
	searchRedirectActionRouterRoute,

	// Routes
	projectDirectoryRouterRoute,
	projectRoute,
	projectSettingsRouterRoute,
	projectDirectoryArchivedRouterRoute,
	projectDirectoryFollowingRouterRoute,
	projectsAppUpdateViewRouterRoute,
	projectsAppSavedViewsUpdatesRouterRoute,
	projectsAppGlobalSearchRoute,
	projectsAppReadingModeRoute,
	appsRoute,

	// Fallback
	popupAuthRoute,
	notFoundRouterRoute,
];

export const noWorkspaceRouteDefinitions = [
	projectNoWorkspaceDirectoryRouterRoute,
	notificationsRouterRoute,
	emptyOrNonProvisionedStatusUpdateStateRouterRoute,
	noWorkspacePersonalSettingsRouterRoute,

	// Fallback
	notFoundRouterRoute,
];

/**
 * Shared pipeline that turns raw route definitions into finalised routes.
 *
 * Two stages run in order:
 * 1. Global routes are expanded into their org/site-prefixed variants.
 * 2. Shared resources (e.g. apps, rovo, sidebar) are attached to every
 *    site or global route so they load alongside the route's own resources.
 *
 * @param definitions  Unprocessed route definitions to transform.
 * @param globalResources  Resources to attach to every site/global route.
 * @returns Finalised routes ready to pass to the router.
 */
function applyRoutePipeline(definitions: HomepageRoutes, globalResources: RouteResource[]): HomepageRoutes {
	return definitions
		.flatMap<HomepageRouteType>(route => {
			if (route.routeType === 'global') {
				return expandGlobalRoute(route);
			}

			return route;
		})
		.map(route => {
			if (route.routeType === 'global' || route.routeType === 'site') {
				route = applyResourcesToRoute(route, globalResources);
			}

			return route;
		});
}

const siteResources = [
	appsResource,
	rovoEntitlementResource,
	searchConfigurationResource,
	capabilityProviderResource,
	projectCapabilitiesResource,
];

export const routes = applyRoutePipeline(routeDefinitions, siteResources);

export const noWorkspaceRoutes = applyRoutePipeline(noWorkspaceRouteDefinitions, [
	appsResource,
	rovoEntitlementResource,
	searchConfigurationResource,
	projectCapabilitiesResource,
]);

/**
 * Builds a variant of the routes array with gated Volt-adapted routes
 * swapped in place of their legacy equivalents.
 * Called lazily from useProjectsRoutes when a Volt route gate is on.
 */
export function buildVoltRoutes(): HomepageRoutes {
	return applyRoutePipeline(routeDefinitions, siteResources);
}
